import { camelCase, isEmpty, startCase } from 'lodash';
import {
  DateRangeFilterItem,
  DateRangeItem,
  FiltersData,
  InputItem,
  ListFilterInput,
  ListFilterKeys,
  Mappings,
  SortField,
  SortFilterInput,
} from '../model';
import { getStatusFilters } from './status-filters';
import { areDatesEqual } from '@zonofi/common';

export const createListFiltersData = <T>(
  data: T,
  mappings: Mappings<T>[]
): Partial<ListFilterInput> | undefined => {
  const listFilter: Partial<ListFilterInput> = {};

  mappings.forEach(
    ({
      dataKey,
      idKey,
      valueKey,
      filterKey,
      multiSelect,
      updateValues = false,
      appliedFilterIds,
    }) => {
      const items = data[dataKey];
      if (Array.isArray(items)) {
        const inputItems: InputItem[] = [];
        if (typeof items[0] === 'string') {
          inputItems.push(
            ...mapStringInput(items, updateValues, appliedFilterIds)
          );
        } else {
          inputItems.push(
            ...mapInputItems(
              items,
              idKey as keyof (typeof items)[0],
              valueKey as keyof (typeof items)[0],
              appliedFilterIds
            )
          );
        }
        listFilter[filterKey as ListFilterKeys] = {
          multiSelect: multiSelect ?? true,
          value: inputItems,
        };
      }
    }
  );

  return listFilter;
};

const mapInputItems = <T>(
  data: T[],
  idKey: keyof T,
  valueKey: keyof T,
  appliedFilterIds: string[] = []
): InputItem[] => {
  return data
    .map((item) => ({
      id: String(item[idKey]),
      value: String(item[valueKey]),
      selected: appliedFilterIds.includes(String(item[idKey])) || false,
    }))
    .filter(({ value }) => !['', 'null', 'undefined'].includes(value));
};

const mapStringInput = (
  data: string[],
  updateValues: boolean,
  appliedFilterIds: string[] = []
): InputItem[] => {
  if (updateValues)
    return getStatusFilters(data, false).filter(({ value }) => !isEmpty(value));
  return data
    .map((x) => ({
      id: x,
      value: startCase(camelCase(x)),
      selected: appliedFilterIds.includes(x) || false,
    }))
    .filter(({ value }) => !isEmpty(value));
};

const updateSortFilterData = (
  oldSort: SortFilterInput,
  newSort: SortFilterInput
): {
  updatedSort: SortFilterInput;
  hasSelectedSort: boolean;
  defaultSort?: SortField;
} => {
  const newSortFilter = { ...oldSort, ...newSort };

  const hasSelectedSort = Object.values(newSortFilter).some(
    ({ selected }) => selected
  );

  const defaultSort = Object.entries(newSortFilter).find(
    ([_, { isDefault }]) => isDefault
  );
  if (!hasSelectedSort && defaultSort) {
    const key = defaultSort[0];
    const sortFilter = defaultSort[1];
    newSortFilter[key] = {
      ...sortFilter,
      selected: sortFilter?.isDefault ?? false,
    };
  }

  let defaultSortField: SortField | undefined = undefined;
  if (defaultSort && defaultSort?.[1]?.isDefault) {
    defaultSortField = {
      field: defaultSort?.[1]?.field,
      direction: defaultSort?.[1]?.isDefault,
    };
  }

  return {
    updatedSort: newSortFilter,
    hasSelectedSort,
    defaultSort: defaultSortField,
  };
};

const updateDateRangeFilterData = (
  oldDateRange?: DateRangeFilterItem,
  newDateRange?: DateRangeFilterItem
): {
  defaultDateRange: DateRangeItem | undefined;
  dateRangeFilter?: DateRangeFilterItem;
} => {
  if (!oldDateRange && !newDateRange) {
    return { defaultDateRange: undefined, dateRangeFilter: undefined };
  }
  const dateRangeFilter: DateRangeFilterItem = {
    defaultRange: newDateRange?.defaultRange || oldDateRange?.defaultRange,
    startDate: newDateRange?.startDate ?? oldDateRange?.startDate ?? new Date(),
    endDate: newDateRange?.endDate ?? oldDateRange?.endDate ?? new Date(),
  };
  let defaultDateRange: DateRangeItem | undefined = undefined;
  if (dateRangeFilter) {
    const { defaultRange } = dateRangeFilter;
    if (defaultRange) {
      const { startDate, endDate } = defaultRange;
      if (
        areDatesEqual(startDate, dateRangeFilter.startDate) &&
        areDatesEqual(endDate, dateRangeFilter.endDate)
      ) {
        defaultDateRange = defaultRange;
      }
    }
  }
  return { defaultDateRange, dateRangeFilter };
};

const mergeFiltersData = (
  oldData: FiltersData,
  newData: FiltersData
): FiltersData => {
  const { booleanFilter, listFilter, sectionFilter, treeFilter } = oldData;

  return {
    booleanFilter: { ...booleanFilter, ...newData.booleanFilter },
    listFilter: { ...listFilter, ...newData.listFilter },
    sectionFilter: { ...sectionFilter, ...newData.sectionFilter },
    treeFilter: { ...treeFilter, ...newData.treeFilter },
  };
};

export const updateFiltersData = (
  oldData: FiltersData,
  newData: FiltersData
): {
  updatedFilter: FiltersData;
  hasSelectedSort: boolean;
  defaultSort?: SortField;
  defaultDateRange?: DateRangeItem;
} => {
  const {
    sortFilter: oldSort,
    dateRange: oldDateRange,
    ...oldFilters
  } = oldData;
  const {
    sortFilter: newSort,
    dateRange: newDateRange,
    ...newFilters
  } = newData;
  const { updatedSort, hasSelectedSort, defaultSort } = updateSortFilterData(
    oldSort || {},
    newSort || {}
  );
  const { defaultDateRange, dateRangeFilter } = updateDateRangeFilterData(
    oldDateRange,
    newDateRange
  );
  const updatedFilter = mergeFiltersData(oldFilters, newFilters);
  updatedFilter.sortFilter = updatedSort;
  updatedFilter.dateRange = dateRangeFilter;

  return { hasSelectedSort, defaultSort, updatedFilter, defaultDateRange };
};
